class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.input?.form) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', () => {
      if (this.hasAttribute('data-ew-search') && this.getQuery() !== this.searchTerm) {
        this.abortController.abort();
        this.removeAttribute('results');
        this.close();
      }
    });
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.input.addEventListener('click', () => {
      if (this.closest('.header__search')?.querySelector(".search-modal__form")) this.open();
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (this.searchTerm.length < (this.hasAttribute('data-ew-search') ? 2 : 1)) {
      this.abortController.abort();
      this.abortController = new AbortController();
      this.removeAttribute('results');
      this.renderEmptyState();
      this.close();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.renderEmptyState();
    }
  }

  async renderEmptyState() {
    if (this.hasAttribute('data-ew-search')) return;
    try {
      const emptySectionId = 'predictive-search-empty';
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.set('section_id', emptySectionId);
      url.searchParams.sort();

      const response = await fetch(`${url.toString()}`, {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();

      const parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search-empty');

      if (parsedEmptySectionMarkup) {
        const emptyStateContent = parsedEmptySectionMarkup.querySelector('#predictive-search-results');

        if (emptyStateContent) {
          this.predictiveSearchResults.innerHTML = emptyStateContent.innerHTML;
          this.setAttribute('results', true);
          this.open();
        }
      }
    } catch (error) {
      if (error?.code === 20) {
        return;
      }
      console.error('Error rendering empty state:', error);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (currentSearchTerm.length < (this.hasAttribute('data-ew-search') ? 2 : 1)) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'Escape':
        this.abortController.abort();
        this.close();
        break;
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'Escape') event.preventDefault();
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }

    // Init predictive search tabs component
    const tabsComponent = this.querySelector('tabs-component');
    if(tabsComponent) {
      tabsComponent.init();
      // this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    };

  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      // if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
      //   // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
      //   return;
      // }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    if (!activeElement) return;
    activeElement.setAttribute('aria-selected', true);
    activeElement.scrollIntoView({block: 'nearest'});
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    if (this.getAttribute('unsupported-locale') === 'true') return;
    this.abortController.abort();
    this.abortController = new AbortController();
    const sectionId = this.hasAttribute('data-ew-search') ? 'ew-search-suggestions' : 'predictive-search';
    const queryKey = sectionId + ':' + searchTerm.toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const requestUrl = new URL(routes.predictive_search_url, window.location.origin);
    requestUrl.searchParams.set('q', searchTerm);
    requestUrl.searchParams.set('resources[limit_scope]', 'each');
    requestUrl.searchParams.set('section_id', sectionId);
    if (this.hasAttribute('data-ew-search')) {
      requestUrl.searchParams.set('resources[type]', 'product,collection');
      requestUrl.searchParams.set('resources[limit]', '5');
    }

    fetch(requestUrl.toString(), {
      signal: this.abortController.signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (response.status === 417) {
          this.setAttribute('unsupported-locale', 'true');
          this.close();
          this.renderEmptyState();
          return null;
        }
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (!text) return;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-' + sectionId)?.innerHTML;
        if (!resultsMarkup || searchTerm !== this.getQuery()) return;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        if (this.contains(document.activeElement)) this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        if (this.getAttribute('unsupported-locale') === 'true') {
          return;
        }
        this.close();
        console.error('Predictive search error:', error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const viewport = window.visualViewport;
    const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
    this.resultsMaxHeight = Math.max(120, Math.min(480, bottom - this.input.getBoundingClientRect().bottom - 16));

    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
